import { useState, useRef, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion } from "framer-motion";
import { Home, Undo2, Trash2, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWindowSize } from "@/hooks/useWindowSize";

import { getVoiceLang } from "@/utils/voicePref";
const TEMPLATES = [
  { id: 1, name: "Kucing Lucu", paths:
    // Ears
    "M58,55 L48,22 L82,42 Z M142,55 L152,22 L118,42 Z " +
    // Inner ears
    "M62,48 L55,32 L72,42 Z M138,48 L145,32 L128,42 Z " +
    // Head
    "M58,80 Q58,135 100,135 Q142,135 142,80 Q142,45 100,45 Q58,45 58,80 Z " +
    // Eyes
    "M82,80 a6,7 0 1,0 12,0 a6,7 0 1,0 -12,0 M106,80 a6,7 0 1,0 12,0 a6,7 0 1,0 -12,0 " +
    // Eye highlights
    "M86,76 a1.5,1.5 0 1,0 3,0 a1.5,1.5 0 1,0 -3,0 M110,76 a1.5,1.5 0 1,0 3,0 a1.5,1.5 0 1,0 -3,0 " +
    // Nose & mouth
    "M94,98 Q100,103 106,98 Q100,108 94,98 Z M88,108 Q100,118 112,108 M100,103 L100,108 " +
    // Whiskers
    "M82,103 L58,98 M82,108 L56,110 M82,113 L58,122 M118,103 L142,98 M118,108 L144,110 M118,113 L142,122 " +
    // Body
    "M68,140 Q70,170 80,182 L120,182 Q130,170 132,140 " +
    // Belly line
    "M82,148 Q100,160 118,148 " +
    // Tail
    "M132,150 Q172,140 168,90 Q166,75 158,80"
  },
  { id: 2, name: "Ikan Ceria", paths:
    // Body
    "M30,100 Q60,40 110,55 Q150,65 160,100 Q150,135 110,145 Q60,160 30,100 Z " +
    // Tail
    "M30,100 L8,75 L18,100 L8,125 Z " +
    // Top fin
    "M85,55 Q100,30 120,50 " +
    // Bottom fin
    "M85,148 Q100,170 120,150 " +
    // Side fin
    "M70,100 Q60,115 80,120 " +
    // Scales (curves)
    "M55,90 Q65,100 55,110 M75,85 Q85,100 75,115 M95,82 Q105,100 95,118 M115,82 Q125,100 115,118 M135,90 Q145,100 135,110 " +
    // Eye
    "M125,85 a6,6 0 1,0 12,0 a6,6 0 1,0 -12,0 M129,85 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 " +
    // Mouth
    "M148,100 Q156,95 156,100 Q156,105 148,100 " +
    // Bubbles
    "M170,60 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0 M175,40 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 M180,25 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0"
  },
  { id: 3, name: "Bunga Matahari", paths:
    // 12 petals around center (using ellipse shapes)
    "M100,30 Q90,55 100,75 Q110,55 100,30 Z " +
    "M170,100 Q145,90 125,100 Q145,110 170,100 Z " +
    "M100,170 Q110,145 100,125 Q90,145 100,170 Z " +
    "M30,100 Q55,110 75,100 Q55,90 30,100 Z " +
    "M150,50 Q130,65 115,80 Q135,75 150,50 Z " +
    "M50,150 Q70,135 85,120 Q65,125 50,150 Z " +
    "M150,150 Q135,130 120,115 Q125,135 150,150 Z " +
    "M50,50 Q65,70 80,85 Q75,65 50,50 Z " +
    // Center circle
    "M100,100 m -22,0 a22,22 0 1,0 44,0 a22,22 0 1,0 -44,0 " +
    // Center dots pattern
    "M92,95 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 M104,95 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 " +
    "M92,107 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 M104,107 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 " +
    // Stem
    "M100,122 L100,190 " +
    // Leaves
    "M100,150 Q70,145 65,165 Q90,165 100,160 M100,165 Q130,160 135,180 Q110,180 100,175"
  },
  { id: 4, name: "Rumah Bahagia", paths:
    // Roof
    "M30,100 L100,40 L170,100 Z " +
    // Body
    "M40,100 L40,170 L160,170 L160,100 " +
    // Door
    "M85,170 L85,135 L115,135 L115,170 " +
    // Door handle
    "M108,152 a1.5,1.5 0 1,0 3,0 a1.5,1.5 0 1,0 -3,0 " +
    // Window left
    "M55,118 L75,118 L75,138 L55,138 Z M65,118 L65,138 M55,128 L75,128 " +
    // Window right
    "M125,118 L145,118 L145,138 L125,138 Z M135,118 L135,138 M125,128 L145,128 " +
    // Chimney with smoke
    "M130,55 L130,80 L145,80 L145,68 " +
    "M137,40 a4,4 0 1,0 0,-1 M148,30 a3,3 0 1,0 0,-1 M156,20 a3,3 0 1,0 0,-1 " +
    // Sun
    "M30,30 a12,12 0 1,0 24,0 a12,12 0 1,0 -24,0 " +
    "M42,12 L42,5 M42,55 L42,62 M14,30 L7,30 M70,30 L77,30 M22,18 L18,14 M62,18 L66,14 M22,42 L18,46 M62,42 L66,46 " +
    // Cloud
    "M165,40 a8,8 0 1,0 0,-2 a10,10 0 1,0 -22,2 a8,8 0 1,0 22,0 Z " +
    // Grass
    "M30,170 Q35,165 40,170 Q45,165 50,170 M150,170 Q155,165 160,170 Q165,165 170,170 " +
    // Heart on door
    "M93,142 a2,2 0 0,1 4,0 a2,2 0 0,1 4,0 Q101,148 97,150 Q93,148 93,142 Z"
  },
  { id: 5, name: "Kupu-Kupu", paths:
    // Body (segmented)
    "M100,55 L100,150 " +
    "M100,60 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    "M100,80 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    "M100,105 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    "M100,130 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    // Antennae
    "M100,55 Q90,40 80,30 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 " +
    "M103,55 Q113,40 123,30 a2,2 0 1,0 4,0 a2,2 0 1,0 -4,0 " +
    // Top left wing
    "M100,80 Q60,40 25,55 Q15,80 35,100 Q60,110 100,100 " +
    // Top right wing
    "M103,80 Q143,40 178,55 Q188,80 168,100 Q143,110 103,100 " +
    // Bottom left wing
    "M100,110 Q70,140 40,160 Q30,135 60,118 Q85,113 100,120 " +
    // Bottom right wing
    "M103,110 Q133,140 163,160 Q173,135 143,118 Q118,113 103,120 " +
    // Wing decoration top
    "M50,75 a5,5 0 1,0 10,0 a5,5 0 1,0 -10,0 M143,75 a5,5 0 1,0 10,0 a5,5 0 1,0 -10,0 " +
    "M75,90 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 M120,90 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    // Wing decoration bottom
    "M65,135 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 M130,135 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0"
  },
  { id: 6, name: "Apel Manis", paths:
    // Apple body (heart-like)
    "M100,55 Q70,50 50,75 Q35,110 60,150 Q80,175 100,178 Q120,175 140,150 Q165,110 150,75 Q130,50 100,55 " +
    // Notch on top (where stem connects)
    "M95,55 Q100,45 105,55 " +
    // Stem
    "M100,45 Q102,30 110,22 " +
    // Leaf
    "M110,22 Q130,15 140,30 Q130,42 115,35 Q108,30 110,22 Z " +
    // Leaf vein
    "M115,28 L132,25 " +
    // Highlight on apple
    "M75,80 Q72,95 80,110 " +
    // Bite mark (cute detail)
    "M125,90 Q135,100 130,115"
  },
  { id: 7, name: "Mobil Imut", paths:
    // Body lower
    "M20,140 L25,160 L175,160 L180,140 Z " +
    // Roof
    "M50,140 L65,100 L135,100 L150,140 " +
    // Window divider
    "M100,100 L100,140 " +
    // Windshield curves
    "M65,100 L60,140 M135,100 L140,140 " +
    // Wheels
    "M40,160 a14,14 0 1,0 28,0 a14,14 0 1,0 -28,0 " +
    "M132,160 a14,14 0 1,0 28,0 a14,14 0 1,0 -28,0 " +
    // Wheel hubs
    "M48,160 a6,6 0 1,0 12,0 a6,6 0 1,0 -12,0 " +
    "M140,160 a6,6 0 1,0 12,0 a6,6 0 1,0 -12,0 " +
    // Headlight
    "M25,150 a4,3 0 1,0 8,0 a4,3 0 1,0 -8,0 " +
    // Taillight
    "M167,150 a4,3 0 1,0 8,0 a4,3 0 1,0 -8,0 " +
    // Door line
    "M80,140 L80,160 " +
    // Door handle
    "M75,150 L78,150 " +
    // Cute eyes (cars have face)
    "M75,118 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    "M119,118 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    // Smile
    "M85,128 Q100,134 115,128 " +
    // Cloud trail
    "M10,135 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0"
  },
  { id: 8, name: "Bintang Senyum", paths:
    // 5-point star
    "M100,20 L120,75 L178,75 L130,108 L150,165 L100,130 L50,165 L70,108 L22,75 L80,75 Z " +
    // Eyes
    "M85,90 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0 " +
    "M107,90 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0 " +
    // Eye highlights
    "M88,87 a1,1 0 1,0 2,0 a1,1 0 1,0 -2,0 " +
    "M110,87 a1,1 0 1,0 2,0 a1,1 0 1,0 -2,0 " +
    // Smile
    "M85,108 Q100,122 115,108 " +
    // Cheeks (rosy)
    "M75,105 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    "M119,105 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 " +
    // Sparkles around star
    "M30,30 L40,40 M40,30 L30,40 " +
    "M170,30 L180,40 M180,30 L170,40 " +
    "M30,170 L40,180 M40,170 L30,180"
  },
];

const COLORS = [
  "#FF0000", "#FF6347", "#FF8C00", "#FFD700", "#FFFF00",
  "#32CD32", "#00AA00", "#006400", "#00CED1", "#1E90FF",
  "#0000FF", "#8A2BE2", "#9B59B6", "#FF69B4", "#FF1493",
  "#8B4513", "#D2691E", "#808080", "#2C3E50", "#FFFFFF",
];

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const Mewarnai = ({ onBackToMenu }) => {
  const { width: winW } = useWindowSize();
  const [currentTemplate, setCurrentTemplate] = useState(0);
  const [selectedColor, setSelectedColor] = useState("#FF0000");
  const [brushSize, setBrushSize] = useState(8);
  const [strokes, setStrokes] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const canvasRef = useRef(null);

  const canvasSize = Math.min(winW - 40, 900);

  const getPos = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: ((clientX - rect.left) / rect.width) * 200, y: ((clientY - rect.top) / rect.height) * 200 };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    setIsDrawing(true);
    setCurrentStroke([pos]);
  };

  const moveDraw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const pos = getPos(e);
    if (!pos) return;
    setCurrentStroke(prev => [...prev, pos]);
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentStroke.length > 1) {
      setStrokes(prev => [...prev, { points: currentStroke, color: selectedColor, size: brushSize }]);
    }
    setCurrentStroke([]);
  };

  const undo = () => setStrokes(prev => prev.slice(0, -1));
  const clear = () => { setStrokes([]); if (soundOn) speakText("Canvas cleared!"); };

  const strokeToPath = (points) => {
    if (points.length < 2) return "";
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  };

  return (
    <div className="flex flex-col items-center w-full mx-auto px-2 pt-3" style={{ maxWidth: Math.max(canvasSize + 40, 600) }} data-testid="mewarnai-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="font-fredoka text-gold font-bold text-sm">🎨 {TEMPLATES[currentTemplate].name}</span>
          <select data-testid="mewarnai-template-select" value={currentTemplate} onChange={e => { setCurrentTemplate(parseInt(e.target.value)); setStrokes([]); }}
            className="glass-card border-gold/20 rounded px-1 py-0.5 text-white font-fredoka text-xs bg-transparent">
            {TEMPLATES.map((t, i) => <option key={t.id} value={i}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex gap-1">
          <Button data-testid="mewarnai-undo" onClick={undo} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Undo2 className="w-3 h-3" /></Button>
          <Button data-testid="mewarnai-clear" onClick={clear} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Trash2 className="w-3 h-3" /></Button>
          <Button data-testid="mewarnai-sound" onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button data-testid="mewarnai-home" onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={canvasRef} className="rounded-2xl border-4 border-amber-400/30 bg-white relative touch-none cursor-crosshair"
        style={{ width: canvasSize, height: canvasSize }}
        onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}>
        <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full">
          {/* Drawn strokes */}
          {strokes.map((s, i) => (
            <path key={i} d={strokeToPath(s.points)} fill="none" stroke={s.color} strokeWidth={s.size / 5} strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {currentStroke.length > 1 && (
            <path d={strokeToPath(currentStroke)} fill="none" stroke={selectedColor} strokeWidth={brushSize / 5} strokeLinecap="round" strokeLinejoin="round" />
          )}
          {/* Template outline on top */}
          <path d={TEMPLATES[currentTemplate].paths} fill="none" stroke="#1f2937" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Color palette */}
      <div className="flex flex-wrap gap-1.5 mt-3 justify-center max-w-md" data-testid="mewarnai-palette">
        {COLORS.map((c, idx) => (
          <button key={c} data-testid={`mewarnai-color-${idx}`} onClick={() => setSelectedColor(c)}
            className={`w-8 h-8 rounded-full border-2 transition-transform ${selectedColor === c ? 'border-white scale-125 shadow-lg' : 'border-gray-400/30'}`}
            style={{ background: c }} />
        ))}
      </div>

      {/* Brush size */}
      <div className="flex items-center gap-3 mt-2" data-testid="mewarnai-brushes">
        <span className="font-fredoka text-gold-light/60 text-xs">Ukuran:</span>
        {[4, 8, 14, 22].map(s => (
          <button key={s} data-testid={`mewarnai-brush-${s}`} onClick={() => setBrushSize(s)}
            className={`rounded-full transition-all ${brushSize === s ? 'ring-2 ring-gold' : ''}`}
            style={{ width: s + 10, height: s + 10, background: selectedColor }} />
        ))}
      </div>
    </div>
  );
};

export default Mewarnai;
